import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * A short code, and a button that copies it.
 *
 * Shared UI rather than promotion-specific: the trusted-sites row shows the same
 * control for the same string, and a feature never imports another feature's
 * internals.
 *
 * The code is rendered as selectable text as well as being copyable: the
 * Clipboard API needs a secure context and a permission that can be refused, and
 * a code the user cannot select by hand when that fails is a code they cannot
 * use.
 */
export function BonusCodeButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);

      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }

      timerRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Refused, or no secure context. The code is on screen and selectable, so
      // there is nothing useful to say — an error toast here would be noise.
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      className={cn(
        'group flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-dashed px-3 py-2 transition',
        copied
          ? 'border-emerald-500/50 bg-emerald-500/10'
          : 'border-surface-600 bg-surface-950/60 hover:border-brand-500/60',
      )}
    >
      <span className="flex min-w-0 flex-col items-start">
        <span className="text-xs uppercase tracking-widest text-surface-500">Bonus kodu</span>
        {/* select-all so a tap-and-hold grabs the whole code, not a word of it. */}
        <span className="select-all truncate font-mono text-sm font-bold text-surface-50">
          {code}
        </span>
      </span>

      <span
        className={cn(
          'shrink-0 text-xs font-medium',
          copied ? 'text-emerald-400' : 'text-surface-400 group-hover:text-brand-400',
        )}
      >
        {copied ? 'Kopyalandı' : 'Kopyala'}
      </span>

      {/* Announces the result without moving focus off the button. */}
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? `${code} panoya kopyalandı` : ''}
      </span>
    </button>
  );
}
